import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { getStudent, updateStudent } from "../controllers/studentController";
import {
  buildStudentUpdatePayload,
  getStudentErrorMessage,
  normalizeStudentDetailsResponse,
  studentToFormData,
} from "../utils/studentUtils";
import { getLmsRole, isParentRole } from "../../utils/lmsRole";
import StudentForm from "../components/StudentForm";
import { StudentsWrapper } from "../styles/StudentsStyle";

const EditStudentPage = () => {
  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { credObj } = useSelector((store) => store.auth);
  const { currentUser } = useSelector((store) => store.users);
  const role = getLmsRole({ currentUser, credObj });
  const isParent = isParentRole(role);

  useEffect(() => {
    let isMounted = true;

    const loadStudent = async () => {
      setIsLoading(true);
      setError("");
      try {
        const response = await getStudent(studentId);
        const student = normalizeStudentDetailsResponse(response);
        if (isMounted) setFormData(studentToFormData(student));
      } catch (err) {
        if (isMounted) setError(getStudentErrorMessage(err?.message));
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadStudent();

    return () => {
      isMounted = false;
    };
  }, [studentId]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError("");
    try {
      await updateStudent(studentId, buildStudentUpdatePayload(formData));
      navigate(`/dashboard/students/${studentId}`, { replace: true });
    } catch (err) {
      setError(getStudentErrorMessage(err?.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <StudentsWrapper>
      <section className="student-page-card">
        <div className="student-page-heading">
          <p className="eyebrow">{isParent ? "Edit Kid" : "Edit Student"}</p>
          <h1>Update learner details</h1>
          <p>Keep grade, age, and credential email accurate for future assignments.</p>
        </div>

        {error && <p className="students-error" role="alert">{error}</p>}
        {isLoading && <p className="students-loading">{isParent ? "Loading kid..." : "Loading student..."}</p>}
        {!isLoading && formData && (
          <StudentForm
            formData={formData}
            onChange={handleChange}
            onSubmit={handleSubmit}
            submitLabel={isParent ? "Save Kid" : "Save Student"}
            isSubmitting={isSubmitting}
            secondaryAction={
              <Link to={`/dashboard/students/${studentId}`} className="student-secondary-action">
                Cancel
              </Link>
            }
          />
        )}
      </section>
    </StudentsWrapper>
  );
};

export default EditStudentPage;
